/**
 * The carousel's previous/next chevrons sit at the vertical centre of the
 * viewport, over the right-hand edge of the overlay's action column. The
 * column is anchored to the bottom of the overlay and grows upwards, so a
 * short panel walks it straight into them (see `.video-panel-carousel__nav`).
 *
 * Rather than let them overlap, the column breaks around the chevrons. The
 * buttons that fit beneath them do not move; the rest are lifted out as a
 * group and sit at the top of the space above, under the overlay's header.
 * The break travels down the column as the panel gets shorter.
 */

/** Chevron diameter (`size="large"` circle) and the margin kept either side. */
const NAV_BUTTON_REM = 2.5;
const NAV_MARGIN_REM = 0.5;

const COLUMN_SELECTOR = ".board-video-overlay__actions";
const HEADER_SELECTOR = ".board-video-overlay__header";

const rootFontSizePx = () =>
	Number.parseFloat(getComputedStyle(document.documentElement).fontSize);

/**
 * Spaces the action buttons in `overlay` around the chevrons centred on it —
 * the overlay covers the same box as the carousel viewport. Pass
 * `obstructed: false` where there is only one clip and so no chevrons to avoid.
 */
export function applyNavClearance(
	overlay: HTMLElement,
	obstructed: boolean,
): void {
	const column = overlay.querySelector<HTMLElement>(COLUMN_SELECTOR);
	if (!column) {
		return;
	}
	const buttons = Array.from(column.children).filter(
		(child): child is HTMLElement => child instanceof HTMLElement,
	);

	// Read the column out in full before writing to it. Button heights, the
	// gap, and the column's bottom edge (it is bottom-aligned, so it grows
	// upwards) all hold still when a margin is applied, which keeps the result
	// the same whether or not a previous pass left margins behind.
	const gap = Number.parseFloat(getComputedStyle(column).rowGap) || 0;
	const heights = buttons.map((button) => button.offsetHeight);
	const overlayBox = overlay.getBoundingClientRect();
	const centre = overlayBox.top + overlayBox.height / 2;
	const halfObstacle =
		((NAV_BUTTON_REM + NAV_MARGIN_REM * 2) * rootFontSizePx()) / 2;
	const obstacleTop = centre - halfObstacle;
	const obstacleBottom = centre + halfObstacle;

	// Lifted buttons stop below the close button rather than at the very top
	// of the overlay, which the header already occupies.
	const header = overlay.querySelector<HTMLElement>(HEADER_SELECTOR);
	const ceiling = header
		? header.getBoundingClientRect().bottom + gap
		: overlayBox.top;

	const margins = buttons.map(() => "");
	let bottom = column.getBoundingClientRect().bottom;
	for (let i = buttons.length - 1; obstructed && i >= 0; i--) {
		const top = bottom - heights[i];
		if (top < obstacleBottom && bottom > obstacleTop) {
			// This button and everything above it move up together, as far as
			// the ceiling — but only where the whole group fits above the
			// chevrons. Overlapping one beats pushing a button off the top.
			const group = heights
				.slice(0, i + 1)
				.reduce((total, height) => total + height + gap, -gap);
			if (ceiling + group <= obstacleTop) {
				margins[i] = `${Math.round(bottom - ceiling - group)}px`;
			}
			break;
		}
		bottom = top - gap;
	}

	buttons.forEach((button, i) => {
		button.style.marginBottom = margins[i];
	});
}
